import { Component, ElementRef, HostBinding, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { trigger, transition, style, animate } from '@angular/animations';
import {
  DwComponent, DwComponentType, DwEventService, DwFilterTileChangedEventData,
  DwFilterTileEvents, DwGridEvents, DwRoutingService, DwSecurityUserService
} from '@devwareapps/devware-cap';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AppMetaDataItemNames, UsersManageUsersCourseProgressGridQueryEntity } from '../../../../meta-data/app-meta-data.service';
import { AppPermissions } from '../../../../meta-data/app-permissions.enum';

interface ManageUsersGridDataLoadEventData {
  itemName?: string;
  allResults?: UsersManageUsersCourseProgressGridQueryEntity[];
}

@DwComponent({
  key: 'ManageUsersTableComponent',
  name: 'Manage Users Table Component',
  componentType: DwComponentType.pageComponent,
  isGlobal: true
})
@Component({
  selector: 'app-manage-users-table',
  templateUrl: './manage-users-table.component.html',
  styleUrls: ['./manage-users-table.component.scss'],
  animations: [
    // Backdrop fade — shared by both bottom sheets
    trigger('backdropFade', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('200ms ease-out', style({ opacity: 1 }))
      ]),
      transition(':leave', [
        animate('150ms ease-out', style({ opacity: 0 }))
      ])
    ]),
    // Parameterized panel reveal — adapts between mobile sheet slide and desktop dropdown
    // Mobile filter uses enterEndTransform: 'none' so native selects anchor correctly (no transform stacking context)
    trigger('panelReveal', [
      transition(':enter', [
        style({ opacity: '{{ startOpacity }}', transform: '{{ startTransform }}' }),
        animate('{{ enterTiming }}', style({ opacity: 1, transform: '{{ enterEndTransform }}' }))
      ], {
        params: {
          startOpacity: '0',
          startTransform: 'scale(0.96)',
          enterTiming: '150ms cubic-bezier(0.23, 1, 0.32, 1)',
          enterEndTransform: 'translateY(0) scale(1)'
        }
      }),
      transition(':leave', [
        animate('{{ leaveTiming }}', style({ opacity: '{{ endOpacity }}', transform: '{{ endTransform }}' }))
      ], {
        params: {
          endOpacity: '0',
          endTransform: 'scale(0.96)',
          leaveTiming: '100ms ease-out'
        }
      })
    ])
  ]
})
export class ManageUsersTableComponent implements OnInit, OnDestroy {
  @HostBinding('style.--mu-content-left') contentLeft: string;
  @HostBinding('style.--mu-content-right') contentRight: string = '0px';

  showTable = false;
  users: UsersManageUsersCourseProgressGridQueryEntity[] = [];
  filteredUsers: UsersManageUsersCourseProgressGridQueryEntity[] = [];
  searchTerm = '';
  sortColumn = 'LastLoginDateTime';
  sortDirection: 'asc' | 'desc' = 'desc';
  selectedTileKey = 'all';
  selectedUserId: number | null = null;
  loading = true;
  showActionsMenu = false;
  showFilterPanel = false;
  filterInstructor = '';
  filterCourse = '';
  filterGroup = '';
  canAssignCourses = false;
  canManageGroups = false;
  openFilterDropdown: string | null = null;

  // Distinct values for filter dropdowns
  instructorOptions: string[] = [];
  courseOptions: string[] = [];
  groupOptions: string[] = [];

  private filterSubscription: Subscription;
  private gridDataSubscription: Subscription;
  private routerSubscription: Subscription;
  private bodyClassObserver: MutationObserver;
  private onMainTransitionEnd: ((e: TransitionEvent) => void) | null = null;
  isMobile = false;
  private onResize = (): void => { this.refreshOffsets(); this.checkMobile(); };

  constructor(
    private elementRef: ElementRef,
    private dwEventService: DwEventService,
    private dwRoutingService: DwRoutingService,
    private dwSecurityUserService: DwSecurityUserService,
    private router: Router
  ) {}

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.showActionsMenu) {
      const dropdown = this.elementRef.nativeElement.querySelector('.mu-actions-dropdown');
      if (dropdown && !dropdown.contains(event.target)) {
        this.showActionsMenu = false;
      }
    }
    if (this.openFilterDropdown) {
      const target = event.target as HTMLElement;
      if (!target.closest('.mu-custom-select')) {
        this.openFilterDropdown = null;
      }
    }
  }

  ngOnInit(): void {
    this.canAssignCourses = this.dwSecurityUserService.hasPermission(AppPermissions.studentCourseCanAssign);
    this.canManageGroups = this.dwSecurityUserService.hasPermission(AppPermissions.studentGroupsCanManage);
    this.checkRoute();

    this.routerSubscription = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.checkRoute();
    });

    this.filterSubscription = this.dwEventService.getEvent(DwFilterTileEvents.filterTileChanged, true).subscribe((event) => {
      this.handleFilterTileChange(event?.data);
    });

    this.gridDataSubscription = this.dwEventService.getEvent(DwGridEvents.gridDataLoad, false).subscribe((event) => {
      this.handleGridDataLoad(event?.data || event);
    });

    this.checkMobile();
    window.addEventListener('resize', this.onResize);

    this.bodyClassObserver = new MutationObserver(() => {
      this.refreshOffsets();
      setTimeout(() => this.refreshOffsets(), 280);
    });
    this.bodyClassObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    const main = document.querySelector('.main');
    if (main) {
      this.onMainTransitionEnd = (e: TransitionEvent): void => {
        if (e.propertyName === 'margin-left' || e.propertyName === 'margin-right') {
          this.refreshOffsets();
        }
      };
      main.addEventListener('transitionend', this.onMainTransitionEnd as EventListener);
    }
  }

  private checkRoute(): void {
    const isManageUsers = this.isManageUsersRoute();

    if (isManageUsers && !this.showTable) {
      this.showTable = true;
      this.loading = true;
      this.refreshOffsets();
      document.body.classList.add('mu-grid-hidden');
      this.hideFrameworkGrid();
    } else if (!isManageUsers && this.showTable) {
      this.showTable = false;
      document.body.classList.remove('mu-grid-hidden');
      this.showFrameworkGrid();
    }
  }

  private isManageUsersRoute(): boolean {
    const url = (this.router.url || '').split('?')[0];
    return url === '/prototypes/manager-users-prototype';
  }

  private handleGridDataLoad(eventData: ManageUsersGridDataLoadEventData): void {
    if (!this.isManageUsersRoute() || !this.showTable) {
      return;
    }

    if (eventData?.itemName !== AppMetaDataItemNames.DwUser) {
      return;
    }

    this.users = [...(eventData?.allResults || [])];
    this.buildFilterOptions();
    this.applyFilters();
    this.loading = false;
    // Re-hide: framework may re-render grid elements after data load
    this.hideFrameworkGrid();
  }

  private hideFrameworkGrid(): void {
    const tryHide = () => {
      const gridContainer = document.querySelector('.dw-grid-container') as HTMLElement;
      const actionsBar = document.querySelector('.dw-grid-actions-bar') as HTMLElement;
      const callouts = document.querySelector('app-manage-user-callouts') as HTMLElement;
      const gridStatusBar = document.querySelector('.dw-grid-status-bar') as HTMLElement;
      const sectionInner = document.querySelector('.dw-section-container-inner') as HTMLElement;
      const sectionContainer = document.querySelector('.dw-section-container') as HTMLElement;
      if (gridContainer) { gridContainer.style.display = 'none'; }
      if (actionsBar) { actionsBar.style.display = 'none'; }
      if (callouts) { callouts.style.display = 'none'; }
      if (gridStatusBar) { gridStatusBar.style.display = 'none'; }
      if (sectionInner) { sectionInner.style.display = 'none'; }
      if (sectionContainer) { sectionContainer.style.overflow = 'hidden'; sectionContainer.style.maxHeight = '0'; }
      if (!gridContainer) { setTimeout(tryHide, 200); }
    };
    setTimeout(tryHide, 50);
  }

  private showFrameworkGrid(): void {
    const gridContainer = document.querySelector('.dw-grid-container') as HTMLElement;
    const actionsBar = document.querySelector('.dw-grid-actions-bar') as HTMLElement;
    const callouts = document.querySelector('app-manage-user-callouts') as HTMLElement;
    const gridStatusBar = document.querySelector('.dw-grid-status-bar') as HTMLElement;
    const sectionInner = document.querySelector('.dw-section-container-inner') as HTMLElement;
    const sectionContainer = document.querySelector('.dw-section-container') as HTMLElement;
    if (gridContainer) { gridContainer.style.display = ''; }
    if (actionsBar) { actionsBar.style.display = ''; }
    if (callouts) { callouts.style.display = ''; }
    if (gridStatusBar) { gridStatusBar.style.display = ''; }
    if (sectionInner) { sectionInner.style.display = ''; }
    if (sectionContainer) { sectionContainer.style.overflow = ''; sectionContainer.style.maxHeight = ''; }
  }

  private checkMobile(): void {
    this.isMobile = window.innerWidth <= 768;
  }

  private refreshOffsets(): void {
    const main = document.querySelector('.main');
    if (main) {
      const style = window.getComputedStyle(main);
      const ml = parseFloat(style.getPropertyValue('margin-left'));
      this.contentLeft = Number.isNaN(ml) ? '250px' : `${ml}px`;
      const mr = parseFloat(style.getPropertyValue('margin-right'));
      this.contentRight = Number.isNaN(mr) || mr <= 0 ? '0px' : `${mr}px`;
    }
  }

  handleFilterTileChange(filterTileEvent: DwFilterTileChangedEventData): void {
    if (filterTileEvent?.item !== AppMetaDataItemNames.DwUser) {
      return;
    }
    const tileKey = filterTileEvent?.selectedTileState?.filterTileConfig?.tileKey;
    if (tileKey) {
      this.selectedTileKey = tileKey;
      this.loading = this.showTable;
      this.applyFilters();
    }
  }

  onSearch(): void {
    this.applyFilters();
  }

  toggleSort(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.applyFilters();
  }

  applyFilters(): void {
    let result = [...this.users];

    // Dropdown filters
    if (this.filterInstructor) {
      result = result.filter(u => u.AssignmentInstructor === this.filterInstructor);
    }
    if (this.filterCourse) {
      result = result.filter(u => (u.Courses || '').includes(this.filterCourse));
    }
    if (this.filterGroup) {
      result = result.filter(u => this.getDelimitedValues(u.Groups).includes(this.filterGroup));
    }

    // Search filter
    if (this.searchTerm.trim()) {
      const term = this.searchTerm.toLowerCase();
      result = result.filter(u => {
        const fullName = `${u.FirstName || ''} ${u.LastName || ''}`.toLowerCase();
        return fullName.includes(term) ||
          (u.Courses || '').toLowerCase().includes(term) ||
          (u.AssignmentInstructor || '').toLowerCase().includes(term) ||
          (u.Groups || '').toLowerCase().includes(term) ||
          (u.Email || '').toLowerCase().includes(term);
      });
    }

    // Sort
    result.sort((a, b) => {
      let valA = a[this.sortColumn] || '';
      let valB = b[this.sortColumn] || '';
      if (typeof valA === 'string') valA = valA.toLowerCase();
      if (typeof valB === 'string') valB = valB.toLowerCase();
      if (valA < valB) return this.sortDirection === 'asc' ? -1 : 1;
      if (valA > valB) return this.sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    this.filteredUsers = result;
    if (!this.filteredUsers.some(user => user.UserId === this.selectedUserId)) {
      this.selectedUserId = null;
    }
  }

  getUserMediaPath(user: UsersManageUsersCourseProgressGridQueryEntity): string {
    return user.DwroMediaMediaPath || user.UserPictureMediaIdDisplay || '';
  }

  selectUser(user: UsersManageUsersCourseProgressGridQueryEntity): void {
    this.selectedUserId = user.UserId;
  }

  viewUser(user: UsersManageUsersCourseProgressGridQueryEntity): void {
    this.dwRoutingService.navigateToUrl(`/manage-users/edit/${user.UserId}`);
  }

  toggleActionsMenu(): void {
    this.showActionsMenu = !this.showActionsMenu;
  }

  closeActionsMenu(): void {
    this.showActionsMenu = false;
  }

  // Animation params: mobile → iOS drawer slide, desktop → origin-aware scale+fade
  get actionsMenuAnim() {
    return this.isMobile
      ? { value: true, params: {
          startOpacity: '1', startTransform: 'translateY(100%)',
          enterTiming: '300ms cubic-bezier(0.32, 0.72, 0, 1)',
          endOpacity: '1', endTransform: 'translateY(100%)',
          leaveTiming: '200ms cubic-bezier(0.32, 0.72, 0, 1)'
        }}
      : { value: true, params: {
          startOpacity: '0', startTransform: 'scale(0.96)',
          enterTiming: '150ms cubic-bezier(0.23, 1, 0.32, 1)',
          endOpacity: '0', endTransform: 'scale(0.96)',
          leaveTiming: '100ms ease-out'
        }};
  }

  get filterPanelAnim() {
    return this.isMobile
      ? { value: true, params: {
          startOpacity: '1', startTransform: 'translateY(100%)',
          enterTiming: '300ms cubic-bezier(0.32, 0.72, 0, 1)',
          endOpacity: '1', endTransform: 'translateY(100%)',
          leaveTiming: '200ms cubic-bezier(0.32, 0.72, 0, 1)'
        }}
      : { value: true, params: {
          startOpacity: '0', startTransform: 'translateY(8px)',
          enterTiming: '200ms cubic-bezier(0.23, 1, 0.32, 1)',
          endOpacity: '0', endTransform: 'translateY(8px)',
          leaveTiming: '120ms ease-out'
        }};
  }

  addJoinRequest(typeId?: number): void {
    this.showActionsMenu = false;
    let url = '/manage-users/join-requests/add';
    if (typeId) { url += `?joinrequesttypeid=${typeId}`; }
    this.dwRoutingService.navigateToUrl(url);
  }

  assignCourses(): void {
    this.showActionsMenu = false;
    this.dwRoutingService.navigateToUrl('/manage-users/assign-courses');
  }

  manageGroups(): void {
    this.showActionsMenu = false;
    this.dwRoutingService.navigateToUrl('/manage-users/student-groups');
  }

  toggleFilterPanel(): void {
    this.showFilterPanel = !this.showFilterPanel;
    if (!this.showFilterPanel) { this.openFilterDropdown = null; }
  }

  toggleFilterDropdown(name: string): void {
    this.openFilterDropdown = this.openFilterDropdown === name ? null : name;
  }

  selectFilterOption(name: string, value: string): void {
    if (name === 'instructor') { this.filterInstructor = value; }
    else if (name === 'course') { this.filterCourse = value; }
    else if (name === 'groups') { this.filterGroup = value; }
    this.openFilterDropdown = null;
    this.applyFilters();
  }

  onFilterChange(): void {
    this.applyFilters();
  }

  clearFilters(): void {
    this.filterInstructor = '';
    this.filterCourse = '';
    this.filterGroup = '';
    this.applyFilters();
  }

  private buildFilterOptions(): void {
    const instructors = new Set<string>();
    const courses = new Set<string>();
    const groups = new Set<string>();
    for (const u of this.users) {
      if (u.AssignmentInstructor) { instructors.add(u.AssignmentInstructor); }
      this.getDelimitedValues(u.Courses).forEach(course => courses.add(course));
      this.getDelimitedValues(u.Groups).forEach(group => groups.add(group));
    }
    this.instructorOptions = Array.from(instructors).sort();
    this.courseOptions = Array.from(courses).sort();
    this.groupOptions = Array.from(groups).sort();
  }

  private getDelimitedValues(value?: string): string[] {
    return (value || '')
      .split(',')
      .map(item => item.trim())
      .filter(item => !!item);
  }

  ngOnDestroy(): void {
    document.body.classList.remove('mu-grid-hidden');
    this.showFrameworkGrid();
    window.removeEventListener('resize', this.onResize);
    if (this.bodyClassObserver) { this.bodyClassObserver.disconnect(); }
    if (this.onMainTransitionEnd) {
      const main = document.querySelector('.main');
      if (main) { main.removeEventListener('transitionend', this.onMainTransitionEnd as EventListener); }
    }
    if (this.filterSubscription) { this.filterSubscription.unsubscribe(); }
    if (this.gridDataSubscription) { this.gridDataSubscription.unsubscribe(); }
    if (this.routerSubscription) { this.routerSubscription.unsubscribe(); }
  }
}
